import { useState, useMemo, useEffect } from 'react';
import { usePermission } from '../../../src/utils/permissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../ui/alert-dialog';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import {
  Users,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Eye,
  AlertTriangle,
  CheckCircle,
  Clock,
  Phone,
  Mail,
  MapPin,
  Upload,
  Save,
  X,
  Power,
  MessageSquare,
  FileText,
  Building,
  CreditCard,
  Trash2
} from 'lucide-react';
import { procurementService } from '../../../src/services/procurement.service';
import { authService } from '../../../src/services/auth.service';
import { documentService } from '../../../src/services/document.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { useOrgCurrency } from '../../../src/hooks/useOrgCurrency';
import { formatCurrency, formatCurrencyCompact } from '../../../src/utils/currency';
import { VendorDetailView } from './VendorDetailView';
import { VendorDetailPage } from './VendorDetailPage';

// Add/Edit Supplier Modal Component
function SupplierModal({ mode, supplier, trigger, onSuccess }: { mode: 'add' | 'edit' | 'view'; supplier?: any; trigger: React.ReactNode; onSuccess?: () => void }) {
  const isReadOnly = mode === 'view';
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    companyName: supplier?.name || '',
    contactPerson: supplier?.contactPerson || '',
    email: supplier?.email || '',
    phone: supplier?.phone || '',
    alternatePhone: supplier?.alternatePhone || '',
    address: supplier?.address || '',
    website: supplier?.website || '',
    taxId: supplier?.taxId || '',
    businessLicense: supplier?.businessLicense || '',
    goodsCategories: supplier?.goodsCategories || [],
    primaryCategory: supplier?.primaryCategory || '',
    creditPeriod: supplier?.creditPeriod || 'net_30',
    creditLimit: supplier?.creditLimit || '',
    paymentMethod: supplier?.paymentMethod || '',
    bankName: supplier?.bankName || '',
    accountNumber: supplier?.accountNumber || '',
    currency: supplier?.currency || 'USD',
    establishedYear: supplier?.establishedYear || '',
    employeeCount: supplier?.employeeCount || '',
    certifications: supplier?.certifications || '',
    insuranceCoverage: supplier?.insuranceCoverage || '',
    specializations: supplier?.specializations || '',
    serviceRadius: supplier?.serviceRadius || '',
    notes: supplier?.notes || '',
    profileDocument: null as File | null
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const goodsCategoryOptions = [
    'Concrete & Cement',
    'Steel & Metals',
    'Aggregates & Sand',
    'Timber & Wood Products',
    'Electrical Supplies',
    'Plumbing & HVAC',
    'Insulation Materials',
    'Roofing Materials',
    'Paint & Coatings',
    'Hardware & Fasteners',
    'Safety Equipment',
    'Tools & Machinery',
    'Glass & Glazing',
    'Flooring Materials',
    'Masonry & Bricks',
    'Waterproofing',
    'Landscaping Materials',
    'Prefabricated Components',
    'Architectural Elements',
    'Specialty Chemicals'
  ];

  const paymentMethods = [
    'Bank Transfer',
    'Check',
    'Credit Card',
    'Online Payment',
    'Cash on Delivery',
    'Letter of Credit',
    'Wire Transfer',
    'ACH Transfer'
  ];

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }
    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact person is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!formData.primaryCategory) {
      newErrors.primaryCategory = 'Vendor type is required';
    }
    if (!formData.paymentMethod) {
      newErrors.paymentMethod = 'Payment method is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError({
        title: 'Validation Error',
        message: 'Please fill in all required fields correctly'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const userProfile = await authService.getUserProfile();

      // Validate organization_id
      if (!userProfile?.organizationId) {
        showError({
          title: 'Organization Error',
          message: 'Organization ID not found. Please log in again.'
        });
        setIsSubmitting(false);
        return;
      }

      // Step 1: Upload document to MinIO if file is selected
      let fileId: string | undefined;
      let filePath: string | undefined;

      if (formData.profileDocument) {
        try {
          const uploadResponse = await documentService.uploadFile(
            formData.profileDocument,
            userProfile.organizationId
          );

          if (uploadResponse.success && uploadResponse.data) {
            fileId = uploadResponse.data.storage_id;
            filePath = uploadResponse.data.storage_id;
          }
        } catch (uploadError: any) {
          console.error('Document upload failed:', uploadError);
          showError({
            title: 'Document Upload Failed',
            message: uploadError?.message || 'Failed to upload company profile document. Supplier will be saved without the document.'
          });
          // Continue without document
        }
      }

      // Step 2: Transform form data to backend format
      // vendor_type should be sent exactly as selected from the dropdown
      const vendorType = formData.primaryCategory as 'material_supplier' | 'subcontractor' | 'equipment_rental' | 'service_provider' | 'distributor' | 'consultant';

      const vendorData = {
        organization_id: userProfile.organizationId,
        vendor_name: formData.companyName,
        vendor_type: vendorType,
        contact_person: formData.contactPerson || undefined,
        phone: formData.phone,
        email: formData.email,
        website: formData.website || undefined,
        address: formData.address,
        tax_id: formData.taxId || undefined,
        license_number: formData.businessLicense || undefined,
        insurance_info: formData.insuranceCoverage || undefined,
        payment_terms: formData.creditPeriod || undefined,
        payment_method_preferred: formData.paymentMethod || undefined,
        credit_limit: formData.creditLimit ? parseFloat(formData.creditLimit) : undefined,
        categories_supplied: formData.goodsCategories.length > 0 ? formData.goodsCategories : undefined,
        specialties: formData.specializations || undefined,  // Backend expects string, not array
        status: 'active' as const,
        is_preferred: false,
        created_by: userProfile.id,
        // Add document fields if upload was successful
        profile_document_id: fileId || undefined,
        profile_document_path: filePath || undefined
      };

      // Log payload for debugging
      console.log('Vendor payload:', JSON.stringify(vendorData, null, 2));
      console.log('vendor_type:', vendorType);
      console.log('categories_supplied:', formData.goodsCategories);

      // Step 3: Save vendor with document references
      if (mode === 'add') {
        await procurementService.createVendor(vendorData);
        showSuccess({
          title: 'Supplier Created',
          message: `${formData.companyName} has been added successfully${fileId ? ' with profile document' : ''}`
        });
      } else if (mode === 'edit') {
        await procurementService.updateVendor(supplier.id, vendorData);
        showSuccess({
          title: 'Supplier Updated',
          message: `${formData.companyName} has been updated successfully${fileId ? ' with profile document' : ''}`
        });
      }

      // Call onSuccess callback to refresh the list, then close the modal
      onSuccess?.();
      setOpen(false);

    } catch (error: any) {
      console.error('Failed to save supplier:', error);
      console.error('Error response:', error?.response?.data);

      // Parse validation errors if available
      let errorMessage = error?.message || 'An unexpected error occurred';
      if (error?.response?.data?.detail) {
        try {
          const details = typeof error.response.data.detail === 'string'
            ? JSON.parse(error.response.data.detail)
            : error.response.data.detail;

          if (Array.isArray(details)) {
            errorMessage = details.map((d: any) => `${d.loc?.join('.')} - ${d.msg}`).join(', ');
          }
        } catch (e) {
          errorMessage = error.response.data.detail;
        }
      }

      showError({
        title: mode === 'add' ? 'Failed to Create Supplier' : 'Failed to Update Supplier',
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategoryToggle = (category: string) => {
    const updatedCategories = formData.goodsCategories.includes(category)
      ? formData.goodsCategories.filter(c => c !== category)
      : [...formData.goodsCategories, category];
    setFormData({...formData, goodsCategories: updatedCategories});
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({...formData, profileDocument: e.target.files[0]});
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild onClick={() => setOpen(true)}>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {mode === 'add' ? 'Add New Supplier' : mode === 'edit' ? 'Edit Supplier' : 'Supplier Details'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'add' ? 'Enter supplier information and business details' : 
             mode === 'edit' ? 'Update supplier information' : 
             'View comprehensive supplier information'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Company Information</h4>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company-name">Company Name *</Label>
                <Input 
                  id="company-name" 
                  value={formData.companyName}
                  onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                  placeholder="ABC Supplies Ltd."
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
                {errors.companyName && <p className="text-sm text-destructive">{errors.companyName}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-person">Contact Person *</Label>
                <Input 
                  id="contact-person" 
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({...formData, contactPerson: e.target.value})}
                  placeholder="John Smith"
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
                {errors.contactPerson && <p className="text-sm text-destructive">{errors.contactPerson}</p>}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input 
                  id="email" 
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="contact@supplier.com"
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
                {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Primary Phone *</Label>
                <Input 
                  id="phone" 
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+1-555-0123"
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
                {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="alternate-phone">Alternate Phone</Label>
                <Input 
                  id="alternate-phone" 
                  value={formData.alternatePhone}
                  onChange={(e) => setFormData({...formData, alternatePhone: e.target.value})}
                  placeholder="+1-555-0124"
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input 
                  id="website" 
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  placeholder="https://www.supplier.com"
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="address">Complete Address *</Label>
              <Textarea 
                id="address" 
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Street address, city, state, postal code, country"
                rows={3}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
              />
              {errors.address && <p className="text-sm text-destructive">{errors.address}</p>}
            </div>
          </div>

          {/* Business Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Business Details</h4>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tax-id">Tax ID / Registration Number</Label>
                <Input 
                  id="tax-id" 
                  value={formData.taxId}
                  onChange={(e) => setFormData({...formData, taxId: e.target.value})}
                  placeholder="Tax identification number"
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="business-license">Business License Number</Label>
                <Input 
                  id="business-license" 
                  value={formData.businessLicense}
                  onChange={(e) => setFormData({...formData, businessLicense: e.target.value})}
                  placeholder="Business license number"
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="established-year">Established Year</Label>
                <Input 
                  id="established-year" 
                  type="number"
                  value={formData.establishedYear}
                  onChange={(e) => setFormData({...formData, establishedYear: e.target.value})}
                  placeholder="2010"
                  min="1900"
                  max="2024"
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="employee-count">Employee Count</Label>
                <Select 
                  value={formData.employeeCount} 
                  onValueChange={(value) => setFormData({...formData, employeeCount: value})}
                  disabled={isReadOnly}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1-10 employees</SelectItem>
                    <SelectItem value="11-50">11-50 employees</SelectItem>
                    <SelectItem value="51-200">51-200 employees</SelectItem>
                    <SelectItem value="201-500">201-500 employees</SelectItem>
                    <SelectItem value="500+">500+ employees</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Goods Categories */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Goods & Services</h4>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="vendor-type">Vendor Type *</Label>
              <Select
                value={formData.primaryCategory}
                onValueChange={(value) => setFormData({...formData, primaryCategory: value})}
                disabled={isReadOnly}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="material_supplier">Material Supplier</SelectItem>
                  <SelectItem value="subcontractor">Subcontractor</SelectItem>
                  <SelectItem value="service_provider">Service Provider</SelectItem>
                  <SelectItem value="equipment_rental">Equipment Rental</SelectItem>
                  <SelectItem value="distributor">Distributor</SelectItem>
                  <SelectItem value="consultant">Consultant</SelectItem>
                </SelectContent>
              </Select>
              {errors.primaryCategory && <p className="text-sm text-destructive">{errors.primaryCategory}</p>}
            </div>
            
            <div className="space-y-2">
              <Label>Additional Categories (Select all that apply)</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 border rounded p-3">
                {goodsCategoryOptions.map(category => (
                  <div key={category} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`category-${category}`}
                      checked={formData.goodsCategories.includes(category)}
                      onChange={() => handleCategoryToggle(category)}
                      className="rounded border-border"
                      disabled={isReadOnly}
                    />
                    <Label htmlFor={`category-${category}`} className="text-sm cursor-pointer">
                      {category}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="specializations">Specializations</Label>
                <Textarea 
                  id="specializations" 
                  value={formData.specializations}
                  onChange={(e) => setFormData({...formData, specializations: e.target.value})}
                  placeholder="Special skills, certifications, or unique capabilities..."
                  rows={2}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service-radius">Service Radius</Label>
                <Input 
                  id="service-radius" 
                  value={formData.serviceRadius}
                  onChange={(e) => setFormData({...formData, serviceRadius: e.target.value})}
                  placeholder="e.g., 50 miles, Local only, National"
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
            </div>
          </div>

          {/* Financial Terms */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Payment & Financial Terms</h4>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="credit-period">Credit Period (Days)</Label>
                <Select 
                  value={formData.creditPeriod} 
                  onValueChange={(value) => setFormData({...formData, creditPeriod: value})}
                  disabled={isReadOnly}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="net_10">Net 10 Days</SelectItem>
                    <SelectItem value="net_15">Net 15 Days</SelectItem>
                    <SelectItem value="net_30">Net 30 Days</SelectItem>
                    <SelectItem value="net_45">Net 45 Days</SelectItem>
                    <SelectItem value="net_60">Net 60 Days</SelectItem>
                    <SelectItem value="cod">Cash on Delivery</SelectItem>
                    <SelectItem value="prepaid">Prepaid</SelectItem>
                    <SelectItem value="due_on_receipt">Due on Receipt</SelectItem>
                    <SelectItem value="milestone_based">Milestone Based</SelectItem>
                    <SelectItem value="progress_based">Progress Based</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="upon_completion">Upon Completion</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="credit-limit">Credit Limit</Label>
                <Input 
                  id="credit-limit" 
                  type="number"
                  value={formData.creditLimit}
                  onChange={(e) => setFormData({...formData, creditLimit: e.target.value})}
                  placeholder="0.00"
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select 
                  value={formData.currency} 
                  onValueChange={(value) => setFormData({...formData, currency: value})}
                  disabled={isReadOnly}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD - US Dollar</SelectItem>
                    <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                    <SelectItem value="GBP">GBP - British Pound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="payment-method">Preferred Payment Method *</Label>
              <Select 
                value={formData.paymentMethod} 
                onValueChange={(value) => setFormData({...formData, paymentMethod: value})}
                disabled={isReadOnly}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map(method => (
                    <SelectItem key={method} value={method}>{method}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.paymentMethod && <p className="text-sm text-destructive">{errors.paymentMethod}</p>}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bank-name">Bank Name</Label>
                <Input 
                  id="bank-name" 
                  value={formData.bankName}
                  onChange={(e) => setFormData({...formData, bankName: e.target.value})}
                  placeholder="Bank name for transfers"
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="account-number">Account Number</Label>
                <Input 
                  id="account-number" 
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({...formData, accountNumber: e.target.value})}
                  placeholder="Bank account number"
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Additional Information</h4>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="certifications">Certifications & Licenses</Label>
                <Textarea 
                  id="certifications" 
                  value={formData.certifications}
                  onChange={(e) => setFormData({...formData, certifications: e.target.value})}
                  placeholder="ISO certifications, industry licenses, quality certifications..."
                  rows={2}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="insurance">Insurance Coverage</Label>
                <Textarea 
                  id="insurance" 
                  value={formData.insuranceCoverage}
                  onChange={(e) => setFormData({...formData, insuranceCoverage: e.target.value})}
                  placeholder="Liability, product, professional insurance details..."
                  rows={2}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Internal Notes</Label>
              <Textarea 
                id="notes" 
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Internal notes about this supplier..."
                rows={3}
                readOnly={isReadOnly}
                className={isReadOnly ? 'bg-muted' : ''}
              />
            </div>
            
            {!isReadOnly && (
              <div className="space-y-2">
                <Label htmlFor="profile-document">Company Profile Document (Optional)</Label>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={() => document.getElementById('profile-document')?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Browse
                  </Button>
                  <div className="flex-1">
                    <Input
                      id="profile-document"
                      type="file"
                      onChange={handleFileUpload}
                      accept=".pdf,.doc,.docx,.jpg,.png"
                      className="hidden"
                    />
                    <Input
                      type="text"
                      value={formData.profileDocument?.name || 'No file chosen'}
                      readOnly
                      className="bg-muted cursor-default"
                      placeholder="No file chosen"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Upload company profile, brochure, or certification documents (PDF, DOC, JPG, PNG - Max 10MB)
                </p>
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" disabled={isSubmitting} onClick={() => setOpen(false)}>Cancel</Button>
          {!isReadOnly && (
            <Button onClick={handleSave} disabled={isSubmitting}>
              <Save className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Saving...' : mode === 'add' ? 'Add Supplier' : 'Update Supplier'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Contact Supplier Modal Component
function ContactSupplierModal({ supplier, trigger }: { supplier: any; trigger: React.ReactNode }) {
  const [contactData, setContactData] = useState({
    subject: '',
    message: '',
    priority: 'normal',
    requestType: 'general'
  });

  const handleSendMessage = () => {
    console.log('Message sent to supplier', { supplier: supplier.id, ...contactData });
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Contact Supplier
          </DialogTitle>
          <DialogDescription>
            Send a message to {supplier.name}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="font-medium">{supplier.name}</p>
            <p className="text-sm text-muted-foreground">{supplier.contactPerson}</p>
            <p className="text-sm text-muted-foreground">{supplier.email}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="request-type">Request Type</Label>
              <Select value={contactData.requestType} onValueChange={(value) => setContactData({...contactData, requestType: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General Inquiry</SelectItem>
                  <SelectItem value="quote">Request Quote</SelectItem>
                  <SelectItem value="order">Order Status</SelectItem>
                  <SelectItem value="quality">Quality Issue</SelectItem>
                  <SelectItem value="payment">Payment Issue</SelectItem>
                  <SelectItem value="delivery">Delivery Issue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={contactData.priority} onValueChange={(value) => setContactData({...contactData, priority: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input 
              id="subject"
              value={contactData.subject}
              onChange={(e) => setContactData({...contactData, subject: e.target.value})}
              placeholder="Enter message subject"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea 
              id="message"
              value={contactData.message}
              onChange={(e) => setContactData({...contactData, message: e.target.value})}
              placeholder="Enter your message here..."
              rows={4}
            />
          </div>
        </div>
        
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={handleSendMessage}>
            <MessageSquare className="h-4 w-4 mr-2" />
            Send Message
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Suppliers Component
export function Suppliers() {
  const { canWrite, canDelete } = usePermission();
  const orgCurrency = useOrgCurrency();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expiringQualifications, setExpiringQualifications] = useState(0);
  const [expiringContracts, setExpiringContracts] = useState(0);
  const [expiringDocuments, setExpiringDocuments] = useState(0);
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [selectedVendor, setSelectedVendor] = useState<any>(null);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const token = authService.getAccessToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      // Get user profile to extract organization_id
      const userProfile = await authService.getUserProfile();
      if (!userProfile.organizationId) {
        setError('Organization not found. Please contact administrator.');
        return;
      }

      // Pass organization_id as a filter parameter
      const response = await procurementService.getVendors(1, 50, {
        organization_id: userProfile.organizationId
      });
      const data = response.data?.data || response.data || [];

      // Transform backend data to match component expectations
      const transformedData = Array.isArray(data) ? data.map(vendor => ({
        id: vendor.vendor_id,
        name: vendor.vendor_name,
        contactPerson: vendor.contact_person || 'N/A',
        email: vendor.email,
        phone: vendor.phone || 'N/A',
        address: `${vendor.address || ''} ${vendor.city || ''} ${vendor.state || ''} ${vendor.zip_code || ''}`.trim() || 'N/A',
        primaryCategory: vendor.vendor_type || 'material_supplier',
        creditPeriod: vendor.payment_terms || 'net_30',
        status: vendor.status || 'active',
        totalOrders: 0, // Will be calculated from orders data
        totalValue: 0, // Will be calculated from orders data
        lastOrder: vendor.updated_at?.split('T')[0] || 'N/A',
        rating: vendor.rating || 4.0
      })) : [];

      setSuppliers(transformedData);
    } catch (err) {
      console.error('Failed to fetch suppliers:', err);
      setError('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const fetchExpiringItems = async () => {
    try {
      const userProfile = await authService.getUserProfile();
      if (!userProfile?.organizationId) return;

      // Fetch expiring vendors (qualifications)
      const vendorsResponse = await procurementService.getVendorsExpiringSoon(userProfile.organizationId, 30);
      setExpiringQualifications(Array.isArray(vendorsResponse.data) ? vendorsResponse.data.length : 0);

      // Fetch expiring contracts
      const contractsResponse = await procurementService.getContractsExpiringSoon(userProfile.organizationId, 60);
      setExpiringContracts(Array.isArray(contractsResponse.data) ? contractsResponse.data.length : 0);

      // Fetch expiring documents
      const documentsResponse = await procurementService.getDocumentsExpiringSoon(userProfile.organizationId, 30);
      setExpiringDocuments(Array.isArray(documentsResponse.data) ? documentsResponse.data.length : 0);
    } catch (err) {
      console.error('Failed to fetch expiring items:', err);
    }
  };

  useEffect(() => {
    fetchSuppliers();
    fetchExpiringItems();
  }, []);

  const handleToggleStatus = async (supplier: any) => {
    try {
      const newStatus = supplier.status === 'active' ? 'inactive' : 'active';
      await procurementService.updateVendor(supplier.id, { status: newStatus });

      showSuccess({
        title: 'Status Updated',
        message: `${supplier.name} has been ${newStatus === 'active' ? 'activated' : 'deactivated'}`
      });

      fetchSuppliers();
    } catch (error: any) {
      showError({
        title: 'Failed to Update Status',
        message: error?.response?.data?.message || error?.message || 'An unexpected error occurred'
      });
    }
  };

  const handleDeleteSupplier = async (supplier: any) => {
    try {
      await procurementService.deleteVendor(supplier.id);

      showSuccess({
        title: 'Supplier Deleted',
        message: `${supplier.name} has been removed from the supplier directory`
      });

      fetchSuppliers();
    } catch (error: any) {
      showError({
        title: 'Failed to Delete Supplier',
        message: error?.response?.data?.message || error?.message || 'An unexpected error occurred'
      });
    }
  };

  const filteredSuppliers = useMemo(() => {
    return suppliers.filter(supplier => {
      const matchesSearch = supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          supplier.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          supplier.primaryCategory.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || supplier.status === filterStatus;
      const matchesCategory = filterCategory === 'all' || supplier.primaryCategory === filterCategory;
      
      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [searchTerm, filterStatus, filterCategory, suppliers]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="h-3 w-3 mr-1" />Active</Badge>;
      case 'inactive':
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200"><X className="h-3 w-3 mr-1" />Inactive</Badge>;
      case 'pending_approval':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200"><Clock className="h-3 w-3 mr-1" />Pending Approval</Badge>;
      case 'suspended':
        return <Badge className="bg-orange-100 text-orange-800 border-orange-200"><AlertTriangle className="h-3 w-3 mr-1" />Suspended</Badge>;
      case 'blacklisted':
        return <Badge className="bg-red-100 text-red-800 border-red-200"><AlertTriangle className="h-3 w-3 mr-1" />Blacklisted</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getRatingStars = (rating: number) => {
    return '★'.repeat(Math.floor(rating)) + '☆'.repeat(5 - Math.floor(rating));
  };

  const handleViewVendor = (supplier: any) => {
    setSelectedVendor(supplier);
    setViewMode('detail');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedVendor(null);
  };

  // If in detail view, show the detail page
  if (viewMode === 'detail' && selectedVendor) {
    return <VendorDetailPage vendor={selectedVendor} onBack={handleBackToList} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-semibold">Supplier Management</h2>
          <p className="text-muted-foreground">Manage supplier relationships and vendor information</p>
        </div>
        {canWrite('/suppliers') && (
          <SupplierModal
            mode="add"
            onSuccess={fetchSuppliers}
            trigger={
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Supplier
              </Button>
            }
          />
        )}
      </div>

      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading suppliers...</p>
        </div>
      )}

      {error && (
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-12 w-12 text-destructive mb-4" />
          <h3 className="text-lg font-medium mb-2">Failed to load suppliers</h3>
          <p className="text-muted-foreground">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600" />
              Total Suppliers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{suppliers.length}</div>
            <p className="text-xs text-blue-600 mt-1">Registered suppliers</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Active Suppliers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{suppliers.filter(s => s.status === 'active').length}</div>
            <p className="text-xs text-green-600 mt-1">Currently active</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-600" />
              Pending Approvals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-700">{suppliers.filter(s => s.status === 'pending_approval').length}</div>
            <p className="text-xs text-yellow-600 mt-1">Awaiting approval</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-purple-600" />
              Total Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">{formatCurrencyCompact(suppliers.reduce((sum: number, s: any) => sum + s.totalValue, 0), orgCurrency)}</div>
            <p className="text-xs text-purple-600 mt-1">Cumulative supplier value</p>
          </CardContent>
        </Card>
      </div>

      {/* Expiring Items Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              Expiring Qualifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-700">{expiringQualifications}</div>
            <p className="text-xs text-orange-600 mt-1">Expiring within 30 days</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-red-600" />
              Expiring Contracts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{expiringContracts}</div>
            <p className="text-xs text-red-600 mt-1">Expiring within 60 days</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-amber-600" />
              Expiring Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-700">{expiringDocuments}</div>
            <p className="text-xs text-amber-600 mt-1">Expiring within 30 days</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search suppliers by name, contact, or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="blacklisted">Blacklisted</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="material_supplier">Material Supplier</SelectItem>
                  <SelectItem value="subcontractor">Subcontractor</SelectItem>
                  <SelectItem value="service_provider">Service Provider</SelectItem>
                  <SelectItem value="equipment_rental">Equipment Rental</SelectItem>
                  <SelectItem value="distributor">Distributor</SelectItem>
                  <SelectItem value="consultant">Consultant</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" size="icon">
                <Filter className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Suppliers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Supplier Directory</CardTitle>
          <CardDescription>
            Showing {filteredSuppliers.length} of {suppliers.length} suppliers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Credit Terms</TableHead>
                  <TableHead>Performance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSuppliers.map(supplier => (
                  <TableRow key={supplier.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div>
                        <p className="font-medium">{supplier.name}</p>
                        <p className="text-sm text-muted-foreground">{supplier.address.split(',')[0]}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="h-3 w-3" />
                          {supplier.contactPerson}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="h-3 w-3" />
                          {supplier.email}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone className="h-3 w-3" />
                          {supplier.phone}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{supplier.primaryCategory}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{supplier.creditPeriod?.replace(/_/g, ' ')}</p>
                        <p className="text-muted-foreground">Payment terms</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-sm">
                          <span className="text-yellow-500">{getRatingStars(supplier.rating)}</span>
                          <span className="text-muted-foreground">({supplier.rating})</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{supplier.totalOrders} orders</p>
                        <p className="text-xs font-medium">{formatCurrencyCompact(supplier.totalValue, orgCurrency)}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(supplier.status)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewVendor(supplier)}
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewVendor(supplier)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {canWrite('/suppliers') && (
                              <SupplierModal
                                mode="edit"
                                supplier={supplier}
                                onSuccess={fetchSuppliers}
                                trigger={
                                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit Supplier
                                  </DropdownMenuItem>
                                }
                              />
                            )}
                            <ContactSupplierModal
                              supplier={supplier}
                              trigger={
                                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                  <MessageSquare className="h-4 w-4 mr-2" />
                                  Contact Supplier
                                </DropdownMenuItem>
                              }
                            />
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                  <Power className="h-4 w-4 mr-2" />
                                  {supplier.status === 'active' ? 'Deactivate' : 'Activate'}
                                </DropdownMenuItem>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="sm:max-w-[425px]">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="flex items-center gap-2">
                                    <Power className="h-5 w-5" />
                                    {supplier.status === 'active' ? 'Deactivate Supplier' : 'Activate Supplier'}
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to {supplier.status === 'active' ? 'deactivate' : 'activate'} <strong>{supplier.name}</strong>?
                                    {supplier.status === 'active'
                                      ? ' This supplier will no longer be available for new orders.'
                                      : ' This supplier will be available for orders again.'}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleToggleStatus(supplier)}
                                    className={supplier.status === 'active' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}
                                  >
                                    {supplier.status === 'active' ? 'Deactivate' : 'Activate'}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                            {canDelete('/suppliers') && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem className="text-destructive" onSelect={(e) => e.preventDefault()}>
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete Supplier
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="sm:max-w-[425px]">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle className="flex items-center gap-2">
                                      <Trash2 className="h-5 w-5 text-destructive" />
                                      Delete Supplier
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete <strong>{supplier.name}</strong>? This action cannot be undone and will remove all associated data.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteSupplier(supplier)} className="bg-destructive hover:bg-destructive/90">
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
        </>
      )}
    </div>
  );
}